import React from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator, Heart, Baby, Syringe, Activity, Pill, Siren, Droplet, HeartPulse,
  Droplets, Flame, Percent, TrendingUp, Thermometer, FlaskConical, Cigarette,
  Bone, ShieldCheck, Moon, Palette, Brain, ClipboardList, Landmark, CalendarDays,
  PillBottle, ArrowRight,
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import { useLanguage } from '../../contexts/LanguageContext';

// key: préfixe de clé i18n (hub_<key>_title / _desc / _cta)
const CATEGORY_DEFS = [
  {
    titleKey: 'hub_cat_emergency',
    color: '#EF4444',
    tools: [
      { key: 'triage_urgence', icon: <Siren className="w-7 h-7" />, to: '/outils-sante/triage-urgence', badgeKey: 'hub_badge_popular' },
      { key: 'fievre', icon: <Thermometer className="w-7 h-7" />, to: '/outils-sante/fievre' },
      { key: 'arret_tabac', icon: <Cigarette className="w-7 h-7" />, to: '/outils-sante/arret-tabac' },
    ],
  },
  {
    titleKey: 'hub_cat_child',
    color: '#14B8A6',
    tools: [
      { key: 'posologie_enfant', icon: <Pill className="w-7 h-7" />, to: '/outils-sante/posologie-enfant', badgeKey: 'hub_badge_popular' },
      { key: 'vaccination', icon: <Syringe className="w-7 h-7" />, to: '/outils-sante/vaccination' },
      { key: 'croissance', icon: <TrendingUp className="w-7 h-7" />, to: '/outils-sante/croissance' },
      { key: 'deshydratation', icon: <Droplets className="w-7 h-7" />, to: '/outils-sante/deshydratation' },
    ],
  },
  {
    titleKey: 'hub_cat_chronic',
    color: '#0596DE',
    tools: [
      { key: 'tension_arterielle', icon: <HeartPulse className="w-7 h-7" />, to: '/outils-sante/tension-arterielle' },
      { key: 'glycemie', icon: <Droplet className="w-7 h-7" />, to: '/outils-sante/glycemie' },
      { key: 'clairance_creatinine', icon: <FlaskConical className="w-7 h-7" />, to: '/outils-sante/clairance-creatinine' },
      { key: 'risque_cardiovasculaire', icon: <Activity className="w-7 h-7" />, to: '/outils-sante/risque-cardiovasculaire' },
      { key: 'bilan_sanguin', icon: <FlaskConical className="w-7 h-7" />, to: '/outils-sante/bilan-sanguin', badgeKey: 'hub_badge_new' },
      { key: 'ramadan_diabete', icon: <Moon className="w-7 h-7" />, to: '/outils-sante/ramadan-diabete', badgeKey: 'hub_badge_new' },
      { key: 'planning_antibiotiques', icon: <ClipboardList className="w-7 h-7" />, to: '/outils-sante/planning-antibiotiques' },
    ],
  },
  {
    titleKey: 'hub_cat_nutrition',
    color: '#F59E0B',
    tools: [
      { key: 'imc', icon: <Calculator className="w-7 h-7" />, to: '/outils-sante/imc', badgeKey: 'hub_badge_popular' },
      { key: 'hydratation', icon: <Droplets className="w-7 h-7" />, to: '/outils-sante/hydratation' },
      { key: 'calories', icon: <Flame className="w-7 h-7" />, to: '/outils-sante/calories' },
      { key: 'masse_grasse', icon: <Percent className="w-7 h-7" />, to: '/outils-sante/masse-grasse' },
      { key: 'calcium_vitamine_d', icon: <Bone className="w-7 h-7" />, to: '/outils-sante/calcium-vitamine-d' },
    ],
  },
  {
    titleKey: 'hub_cat_women',
    color: '#EC4899',
    tools: [
      { key: 'ovulation', icon: <Heart className="w-7 h-7" />, to: '/outils-sante/ovulation' },
      { key: 'grossesse', icon: <Baby className="w-7 h-7" />, to: '/outils-sante/grossesse', badgeKey: 'hub_badge_popular' },
      { key: 'prise_poids_grossesse', icon: <TrendingUp className="w-7 h-7" />, to: '/outils-sante/prise-poids-grossesse' },
      { key: 'bilan_prenuptial', icon: <ShieldCheck className="w-7 h-7" />, to: '/outils-sante/bilan-prenuptial', badgeKey: 'hub_badge_new' },
    ],
  },
  {
    titleKey: 'hub_cat_wellbeing',
    color: '#8B5CF6',
    tools: [
      { key: 'stress', icon: <Brain className="w-7 h-7" />, to: '/outils-sante/stress' },
      { key: 'cycles_sommeil', icon: <Moon className="w-7 h-7" />, to: '/outils-sante/cycles-sommeil' },
      { key: 'perception_couleurs', icon: <Palette className="w-7 h-7" />, to: '/outils-sante/perception-couleurs' },
      { key: 'doublons_medicaments', icon: <PillBottle className="w-7 h-7" />, to: '/outils-sante/doublons-medicaments', badgeKey: 'hub_badge_new' },
    ],
  },
  {
    titleKey: 'hub_cat_admin',
    color: '#6366F1',
    tools: [
      { key: 'simulateur_cnas', icon: <Landmark className="w-7 h-7" />, to: '/outils-sante/simulateur-cnas' },
      { key: 'conge_maternite', icon: <CalendarDays className="w-7 h-7" />, to: '/outils-sante/conge-maternite', badgeKey: 'hub_badge_new' },
    ],
  },
];

const HealthToolsHub = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <section className="bg-gradient-to-r from-[#0596DE] to-[#047CBA] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">{t('hub_page_title')}</h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {t('hub_page_subtitle')}
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
          {CATEGORY_DEFS.map((cat, ci) => (
            <div key={ci}>
              <div className="flex items-center gap-2 mb-5">
                <span className="w-2 h-5 rounded-full" style={{ backgroundColor: cat.color }} />
                <h2 className="text-lg font-bold text-[#0F172A]">{t(cat.titleKey)}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                {cat.tools.map((tool, i) => (
                  <Link
                    key={i}
                    to={tool.to}
                    className="relative bg-white rounded-2xl p-6 border border-[#E2E8F0] hover:shadow-md transition-all flex flex-col"
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${cat.color}50`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; }}
                  >
                    {tool.badgeKey && (
                      <span
                        className="absolute top-4 right-4 text-[11px] font-semibold px-2 py-0.5 rounded-full"
                        style={{
                          color: tool.badgeKey === 'hub_badge_new' ? '#047857' : '#B45309',
                          backgroundColor: tool.badgeKey === 'hub_badge_new' ? '#D1FAE5' : '#FEF3C7',
                        }}
                      >
                        {t(tool.badgeKey)}
                      </span>
                    )}
                    <div
                      className="mb-4 w-14 h-14 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${cat.color}15`, color: cat.color }}
                    >
                      {tool.icon}
                    </div>
                    <h3 className="font-semibold text-[#0F172A] mb-2">{t(`hub_${tool.key}_title`)}</h3>
                    <p className="text-sm text-[#64748B] leading-relaxed flex-1">{t(`hub_${tool.key}_desc`)}</p>
                    <div
                      className="mt-4 inline-flex items-center gap-1 text-sm font-medium"
                      style={{ color: cat.color }}
                    >
                      {t(`hub_${tool.key}_cta`) || t('hub_cta_discover')} <ArrowRight className="w-4 h-4" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}

          <p className="text-xs text-[#94A3B8] text-center max-w-2xl mx-auto">
            {t('hub_disclaimer')}
          </p>
        </div>
      </section>

      <footer className="bg-[#0F172A] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-bold text-xl font-['Outfit'] text-[#0596DE]">Tabbibak.dz</span>
          <p className="text-gray-400 text-sm mt-3">{t('hub_footer_tagline')}</p>
          <div className="border-t border-gray-800 mt-6 pt-6 text-gray-500 text-sm">
            © {new Date().getFullYear()} Tabbibak.dz
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HealthToolsHub;
